import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserProfileService } from 'src/app/core/services/user.service';
import { NgForm } from '@angular/forms';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss']
})
export class CheckoutComponent implements OnInit, OnDestroy {

  breadCrumbItems!: Array<{}>;
  submitted = false;
  loading = true;
  showRetryPopup = false;

  customerDetails: any = {};
  partnerDetails: any;
  orderDetails: any;
  productDetails: any;
  otpSubmitting: boolean = false;
  otpTimer: number = 30;
  resendEnabled: boolean = false;
  otpInterval: any;

  private hasFetchedDetails: boolean = false;

  @ViewChild('content') contentModalRef!: ElementRef;
  @ViewChild('retryModal') retryModalRef!: ElementRef;

  billingInfo: any = {
    first_name: '',
    last_name: '',
    pincode: '',
    city: '',
    state: '',
    address1: '',
    address2: '',
    landmark: ''
  };

  orderId: string = '';
  token: string = '';

  otpCode: string = '';
  otpError: string = '';

  constructor(
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private http: HttpClient,
    private userProfileService: UserProfileService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: 'Ecommerce' },
      { label: 'Checkout', active: true }
    ];

    this.orderId = this.route.snapshot.queryParamMap.get('orderId') || '';
    this.token = this.route.snapshot.queryParamMap.get('token') || '';

    if (this.orderId && this.token && !this.hasFetchedDetails) {
      this.hasFetchedDetails = true;
      this.fetchCustomerDetails(this.orderId, this.token);
    } else {
      this.router.navigate(['/others/error']);
    }
  }

  fetchCustomerDetails(orderId: string, token: string): void {
    const url = `${environment.apiBase}/customerdetails/orderid/${orderId}`;
    const body = { token };

    this.loading = true;

    this.http.post<any>(url, body).subscribe({
      next: (res) => {
        if (res?.status?.toLowerCase() === 'success' && res?.data) {
          const data = res.data;

          this.customerDetails = {
            EMAIL_ID: data.employee_email_id,
            MOBILE_NO: data.mobile_no,
            EMP_NAME: data.employee_name,
            EMP_NUMBER: data.employee_number,
            FEEDBACK: data.feedback
          };

          this.orderDetails = {
            pointUsed: data.point_used,
            amountUsed: data.amount_used,
            currencyName: data.currency_name,
          };

          this.productDetails = {
            productPrice: data.product_price,
            discountAmount: data.discount_amount,
            quantity: data.quantity,
            total: data.total,
            name: data.product_name,
            skuName: data.sku_name,
            category: data.category_name,
            porderid: data.partner_order_number,
            cfee: data.convenienceFee,
          };

          this.userProfileService.setAvailablePoints(data.available_points || '0');
          this.userProfileService.setCoinValue(data.coin_value || '0.00');
          this.userProfileService.setEmpName(data.employee_name || '');
          this.userProfileService.setCurrencyName(data.currency_name || '0.00');
        } else {
          this.router.navigate(['/others/error']);
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('API error:', err);
        this.loading = false;
        this.router.navigate(['/others/error']);
      }
    });
  }

  openModal(content: any): void {
    this.resetOtpData();
    this.submitted = false;
    this.sendOtpRequest();
  }

  proceedToPay(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    // Proceed to send OTP or payment logic
    this.sendOtpRequest();
  }

  sendOtpRequest(): void {
    // Validate required billing fields before sending OTP
    const requiredFields = [
      'first_name', 'last_name', 'phone', 'pincode', 'city', 'state', 'address1'
    ];
    for (const field of requiredFields) {
      if (!this.billingInfo[field] || this.billingInfo[field].toString().trim() === '') {
        this.otpError = `Please fill in your ${field.replace('_', ' ')}.`;
        this.triggerRetryPopup();
        return;
      }
    }

    this.otpSubmitting = true; // show loader

    const body = {
      order_id: Number(this.orderId),
      first_name: this.billingInfo.first_name,
      last_name: this.billingInfo.last_name,
      phone: this.billingInfo.phone,
      pincode: this.billingInfo.pincode,
      city: this.billingInfo.city,
      state: this.billingInfo.state,
      address1: this.billingInfo.address1,
      address2: this.billingInfo.address2,
      landmark: this.billingInfo.landmark,
      token: this.token
    };

    const url = `${environment.apiBase}/otp`;

    this.http.post<any>(url, body).subscribe({
      next: (res) => {
        this.otpSubmitting = false;

        if (res?.status?.toLowerCase() === 'success') {
          this.modalService.open(this.contentModalRef, { centered: true });
          this.startOtpCountdown();
        } else {
          this.otpError = res?.msg || 'Failed to send OTP. Please try again.';
          this.triggerRetryPopup();
        }
      },
      error: () => {
        this.otpSubmitting = false;
        this.otpError = 'Network error. Please try again.';
        this.triggerRetryPopup();
      }
    });
  }

  startOtpCountdown(): void {
    this.otpTimer = 30;
    this.resendEnabled = false;

    this.otpInterval = setInterval(() => {
      this.otpTimer--;

      if (this.otpTimer <= 0) {
        this.resendEnabled = true;
        clearInterval(this.otpInterval);
      }
    }, 1000);
  }


  resendOtpOnly(): void {
    if (!this.resendEnabled) return;

    this.resetOtpData();

    const url = `${environment.apiBase}/otp/resend/zillionorderid/${this.orderId}`;

    this.http.get<any>(url).subscribe({
      next: (res) => {
        if (res?.status?.toLowerCase() === 'success') {
          this.startOtpCountdown();
        } else {
          this.triggerRetryPopup();
        }
      },
      error: () => {
        this.triggerRetryPopup();
      }
    });
  }

  

  resetOtpInput() {
    this.otpCode = '';       // Clear the input
    this.otpError = '';      // Clear any error
  }

  resendOtp(): void {
    if (this.resendEnabled) {
      this.sendOtpRequest();  // reuse your existing OTP API call
      this.startOtpCountdown(); // restart countdown
    }
  }

  clearOtpTimer(): void {
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
  }


  submitOtp(modalRef: any): void {
    if (!this.otpCode || this.otpCode.trim().length === 0) {
      this.otpError = 'Please enter the OTP.';
      return;
    }

    const url = `${environment.apiBase}/hold-coin`;
    const body = {
      order_id: Number(this.orderId),
      otp: this.otpCode.trim(),
      token: this.token
    };

    this.otpSubmitting = true;
    this.otpError = '';

    this.http.post<any>(url, body).subscribe({
      next: (res) => {
        this.otpSubmitting = false;

        if (res?.status?.toLowerCase() === 'success') {
          if (res.message === 'PG') {
            modalRef.close();
            this.resetOtpData();
            this.initiateRazorpayFlow();
          } else {
            modalRef.close();
            this.resetOtpData();
            this.router.navigate(['/pages/shop/order-confirm'], {
              queryParams: {
                orderId: this.productDetails?.porderid,
                F: this.customerDetails?.FEEDBACK,
                id: this.orderId,
                token: this.token
              }
            });
          }
        } else {
          this.otpError = res?.msg || 'Incorrect OTP, please try again.';
        }
      },
      error: (err) => {
        this.otpSubmitting = false;
        console.error('OTP Error:', err);
        this.otpError = 'Something went wrong. Please try again.';
      }
    });
  }

// PG With Loader
// initiateRazorpayFlow(): void {
//   const tokenUrl = `${environment.apiBaseAuth}/token`;
//   const checkoutUrl = `${environment.apiBasePay}/checkout?gatewayName=razorpay`;
//   const pgProcessUrl = `${environment.apiBase}/pg-process/orderid/${this.orderId}`;

//   const roundedAmount = Math.round(this.orderDetails.amountUsed);

//   this.http.post<any>(tokenUrl, {
//     merchant_id: environment.AuthToken.merchant_id,
//     secret_key: environment.AuthToken.secret_key
//   }).subscribe({
//     next: (res1) => {
//       if (res1?.status === 'success' && res1.token) {
//         const authToken = res1.token;

//         this.http.post<any>(checkoutUrl, {
//           paymentType: 'zillion',
//           amount: roundedAmount * 100,
//           currency: 'INR',
//           customerCode: this.orderId,
//           paymentId: this.orderId
//         }, {
//           headers: {
//             Authorization: `Bearer ${authToken}`
//           }
//         }).subscribe({
//           next: (res2) => {
//             if (res2?.status === 'success' && res2.data) {
//               const rzpOptions = {
//                 key: res2.data.secretToken,
//                 amount: roundedAmount * 100,
//                 currency: 'INR',
//                 name: 'Lakee e Shoping india pvt ltd',
//                 description: 'Zillion Transaction',
//                 image: 'https://lakeeshop.com/img/logo_lakee.png',
//                 order_id: res2.data.checkoutToken,
//                 callback_url: '', // not used since we’re handling success manually
//                 prefill: {
//                   name: this.billingInfo.first_name,
//                   email: this.customerDetails.EMAIL_ID,
//                   contact: this.customerDetails.MOBILE_NO
//                 },
//                 notes: {
//                   address: this.billingInfo.address1
//                 },
//                 theme: {
//                   color: '#3399cc'
//                 },
//                 handler: (response: any) => {
//                   // const razorpayPaymentId = response.razorpay_payment_id;
//                   // const razorpayOrderId = response.razorpay_order_id;
//                   // const razorpaySignature = response.razorpay_signature;

//                   this.loading = true;
//                   this.http.get<any>(pgProcessUrl).subscribe({
//                     next: (pgRes) => {
//                       if (pgRes?.status === 'success') {
//                         //const orderConfirmUrl = `/pages/shop/order-confirm?orderId=${this.productDetails?.porderid}&F=${this.customerDetails?.FEEDBACK}&id=${this.orderId}&token=${this.token}&razorpay_payment_id=${razorpayPaymentId}&razorpay_order_id=${razorpayOrderId}&razorpay_signature=${razorpaySignature}`;
//                         const orderConfirmUrl = `/pages/shop/order-confirm?orderId=${this.productDetails?.porderid}&F=${this.customerDetails?.FEEDBACK}&id=${this.orderId}&token=${this.token}`;
//                         this.router.navigateByUrl(orderConfirmUrl);
//                       } else {
//                         this.loading = false;
//                         this.router.navigate(['/others/error']);
//                       }
//                     },
//                     error: () => {
//                       this.loading = false;
//                       this.router.navigate(['/others/error']);
//                     }
//                   });
//                 }
//               };

//               const rzp = new (window as any).Razorpay(rzpOptions);

//               rzp.on('payment.failed', (response: any) => {
//                 console.error('Payment failed:', response);
//                 this.router.navigate(['/others/error']);
//               });

//               rzp.open();
//             } else {
//               alert('Failed to initiate Razorpay payment.');
//               console.error(res2);
//             }
//           },
//           error: (err) => {
//             console.error('Checkout Token Error:', err);
//           }
//         });
//       } else {
//         console.error('Failed to get auth token');
//       }
//     },
//     error: (err) => {
//       console.error('Token Generation Error:', err);
//     }
//   });
// }


// PG With Loader
initiateRazorpayFlow(): void {
  const tokenUrl = `${environment.apiBaseAuth}/token`;
  const checkoutUrl = `${environment.apiBasePay}/checkout?gatewayName=razorpay`;

  const roundedAmount = Math.round(this.orderDetails.amountUsed);

  this.http.post<any>(tokenUrl, {
    merchant_id: environment.AuthToken.merchant_id,
    secret_key: environment.AuthToken.secret_key
  }).subscribe({
    next: (res1) => {
      if (res1?.status === 'success' && res1.token) {
        const authToken = res1.token;

        this.http.post<any>(checkoutUrl, {
          paymentType: 'zillion',
          amount: roundedAmount * 100,
          currency: 'INR',
          customerCode: this.orderId,
          paymentId: this.orderId
        }, {
          headers: {
            Authorization: `Bearer ${authToken}`
          }
        }).subscribe({
          next: (res2) => {
            if (res2?.status === 'success' && res2.data) {
              const rzpOptions = {
                key: res2.data.secretToken,
                amount: roundedAmount * 100,
                currency: 'INR',
                name: 'Lakee e Shoping india pvt ltd',
                description: 'Zillion Transaction',
                image: 'https://lakeeshop.com/img/logo_lakee.png',
                order_id: res2.data.checkoutToken,
                callback_url: '',
                prefill: {
                  name: this.billingInfo.first_name,
                  email: this.customerDetails.EMAIL_ID,
                  contact: this.customerDetails.MOBILE_NO
                },
                notes: {
                  address: this.billingInfo.address1
                },
                theme: {
                  color: '#3399cc'
                },
                handler: (response: any) => {
                  // console.log('Razorpay Success Response:', response);
                  const razorpayPaymentId = response.razorpay_payment_id;
                  // console.log('Razorpay Payment ID:', razorpayPaymentId);

                  const pgProcessUrl = `${environment.apiBase}/pg-process/orderid/${this.orderId}/paymentid/${razorpayPaymentId}`;

                  this.loading = true;

                  this.http.get<any>(pgProcessUrl).subscribe({
                    next: (pgRes) => {
                      // console.log('PG Process API Response:', pgRes);

                      if (pgRes?.status === 'success') {
                        const orderConfirmUrl = `/pages/shop/order-confirm?orderId=${this.productDetails?.porderid}&F=${this.customerDetails?.FEEDBACK}&id=${this.orderId}&token=${this.token}`;
                        this.router.navigateByUrl(orderConfirmUrl);
                      } else {
                        this.loading = false;
                        this.router.navigate(['/others/error']);
                      }
                    },
                    error: (err) => {
                      console.error('PG Process API Error:', err);
                      this.loading = false;
                      this.router.navigate(['/others/error']);
                    }
                  });
                }
              };

              const rzp = new (window as any).Razorpay(rzpOptions);

              rzp.on('payment.failed', (response: any) => {
                console.error('Payment failed:', response);
                this.router.navigate(['/others/error']);
              });

              rzp.open();
            } else {
              alert('Failed to initiate Razorpay payment.');
              console.error(res2);
            }
          },
          error: (err) => {
            console.error('Checkout Token Error:', err);
          }
        });
      } else {
        console.error('Failed to get auth token');
      }
    },
    error: (err) => {
      console.error('Token Generation Error:', err);
    }
  });
}


  resetOtpData(): void {
    this.otpCode = '';
    this.otpError = '';
    this.otpTimer = 30;
    this.otpSubmitting = false;
    this.resendEnabled = false;
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
      this.otpInterval = null;
    }
  }

  triggerRetryPopup(): void {
    this.showRetryPopup = true;
    setTimeout(() => {
      this.showRetryPopup = false;
    }, 5000);
  }

  ngOnDestroy(): void {
    console.log('CheckoutComponent destroyed');

    // Clear OTP countdown timer if it exists
    if (this.otpInterval) {
      clearInterval(this.otpInterval);
    }
  }
  
}